import { ArrowUp, Delete } from '@factorialco/f0-react/icons/app'
import { PaymentRequestDetailFieldsFragment } from 'generated/resources/graphql'

import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'
import { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'

type StatusColor = 'neutral' | 'info' | 'positive' | 'warning'

const getStatusColor = (status: string): StatusColor => {
  const statusColorMap: Record<string, StatusColor> = {
    init: 'warning',
    in_payment: 'info',
    in_progress: 'info',
    partially_paid: 'warning',
    paid: 'positive',
  }
  return statusColorMap[status] ?? 'neutral'
}

const getStatusLabel = (status: string): string => {
  const statusLabelMap: Record<string, string> = {
    init: i18n.t('banking.paymentRequests.status.init'),
    in_payment: i18n.t('banking.paymentRequests.status.inPayment'),
    in_progress: i18n.t('banking.paymentRequests.status.inProgress'),
    partially_paid: i18n.t('banking.paymentRequests.status.partiallyPaid'),
    paid: i18n.t('banking.paymentRequests.status.paid'),
  }
  return statusLabelMap[status] ?? status
}

export const buildPaymentRequestHeaderComponentProps = (
  paymentRequest: PaymentRequestDetailFieldsFragment
): ResourceHeadersPropsWithActions => {
  const canSendToPay = paymentRequest.status === 'init'
  const nonDeleteableStatuses = ['paid', 'in_payment', 'in_progress']
  const canDelete = !nonDeleteableStatuses.includes(paymentRequest.status)

  const secondaryActions: Array<{
    label: string
    icon: typeof Delete
    key: string
    critical?: boolean
  }> = []

  if (canDelete) {
    secondaryActions.push({
      label: i18n.t('banking.paymentRequests.actions.delete'),
      icon: Delete,
      key: 'delete_payment_request',
      critical: true,
    })
  }

  return {
    title: paymentRequest.label ?? '',
    status: {
      label: '',
      text: getStatusLabel(paymentRequest.status),
      variant: getStatusColor(paymentRequest.status),
    },
    primaryAction: canSendToPay
      ? {
          label: i18n.t('banking.paymentRequests.actions.send_to_pay'),
          key: 'send_to_pay',
          icon: ArrowUp,
        }
      : undefined,
    secondaryActions,
    metadata: [
      {
        label: i18n.t('banking.paymentRequests.detail.totalAmount'),
        value: {
          type: 'text',
          content: formatCurrency(paymentRequest.amountCents, paymentRequest.currency),
        },
      },
      {
        label: i18n.t('banking.paymentRequests.detail.requester'),
        value: {
          type: 'text',
          content: paymentRequest.requester?.access?.fullName ?? '-',
        },
      },
      ...(paymentRequest.dueDate
        ? [
            {
              label: i18n.t('banking.paymentRequests.detail.dueDate'),
              value: {
                type: 'text' as const,
                content: new Date(paymentRequest.dueDate).toLocaleDateString(),
              },
            },
          ]
        : []),
    ],
  }
}
